import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertTriangle } from "lucide-react";
import {
  CreateProductionOrderRequestSchema,
  type CreateProductionOrderRequest,
} from "@opus-dt/shared-types";
import { toast } from "@/components/ui/notify";
import { ProductionOrderFormActions } from "./ProductionOrderFormActions";
import { ProductionOrderFormBody } from "./ProductionOrderFormBody";
import { useCreateProductionOrder, useUpdateProductionOrder } from "./hooks";
import { isApiClientError } from "@/lib/api-client";
import type { ProductionOrder } from "../../types/production-orders.types.js";

// UX preventiva (criação): "início no futuro" é enforced com 400 no backend; aqui é só
// preventivo. NÃO se aplica na edição — editar uma OP SCHEDULED recém-vencida (início no passado) é
// permitido pelo backend (UpdateProductionOrderRequestSchema não tem essa refine).
export const ProductionOrderFormSchema = CreateProductionOrderRequestSchema.refine(
  (o) => new Date(o.plannedStartAt).getTime() > Date.now(),
  { path: ["plannedStartAt"], message: "O início planejado deve ser no futuro." }
);

function defaultsFrom(
  order?: ProductionOrder,
  lockedLineId?: string
): Partial<CreateProductionOrderRequest> {
  if (!order) {
    return {
      lineId: lockedLineId ?? "",
      productIds: [],
      primaryProductId: "",
      plannedStartAt: "",
      expectedEnd: "",
      // Prazo opcional: nasce vazio (null), não "hoje" — inventar um prazo que o
      // cliente não deu tornaria toda OP nova "no prazo" por acidente.
      dueDate: null,
      code: "",
      responsible: "",
      observations: "",
      characteristics: [],
    };
  }
  return {
    lineId: order.lineId,
    productIds: order.products.map((p) => p.productId),
    primaryProductId:
      order.products.find((p) => p.isPrimary)?.productId ?? order.products[0]?.productId ?? "",
    plannedStartAt: order.plannedStartAt,
    expectedEnd: order.expectedEnd,
    dueDate: order.dueDate,
    plannedQty: order.plannedQty,
    code: order.code ?? "",
    responsible: order.responsible ?? "",
    observations: order.observations ?? "",
    characteristics: order.characteristics.map((c) =>
      c.valueId
        ? { characteristicId: c.characteristicId, valueId: c.valueId }
        : { characteristicId: c.characteristicId, text: c.label }
    ),
  };
}

interface Props {
  mode: "create" | "edit";
  order?: ProductionOrder;
  onDone: () => void;
  onCancel: () => void;
  onCancelOrder?: () => void;
  /** Reporta o dirty do form ao container (guarda de descarte no ModalOverlay). */
  onDirtyChange?: (dirty: boolean) => void;
  /** Origem detalhe da linha: pré-seleciona e trava a linha (criação). */
  lockedLineId?: string;
}

export function ProductionOrderForm({
  mode,
  order,
  onDone,
  onCancel,
  onCancelOrder,
  onDirtyChange,
  lockedLineId,
}: Props) {
  const create = useCreateProductionOrder();
  const update = useUpdateProductionOrder();
  const [conflict, setConflict] = useState<string | null>(null);
  // C-6: na EDIÇÃO não aplicamos a refine "início no futuro" — editar campos de uma OP SCHEDULED já
  // vencida é permitido pelo backend (UpdateProductionOrderRequestSchema, sem essa refine). O form de
  // edição vem 100% preenchido, então usamos o CreateProductionOrderRequestSchema NÃO-parcial (mesmas
  // refines end>início + principal∈lista, shape completo p/ casar o tipo). Criação mantém a refine.
  const resolverSchema =
    mode === "edit" ? CreateProductionOrderRequestSchema : ProductionOrderFormSchema;
  const form = useForm<CreateProductionOrderRequest>({
    resolver: zodResolver(resolverSchema),
    defaultValues: defaultsFrom(order, lockedLineId) as CreateProductionOrderRequest,
  });
  const pending = create.isPending || update.isPending;
  // Lido em render p/ assinar o proxy do RHF; propagado ao container p/ guarda de descarte.
  const isDirty = form.formState.isDirty;
  useEffect(() => onDirtyChange?.(isDirty), [isDirty, onDirtyChange]);

  async function submit(values: CreateProductionOrderRequest) {
    setConflict(null);
    try {
      if (mode === "edit" && order) await update.mutateAsync({ id: order.id, data: values });
      else await create.mutateAsync(values);
      toast.success(mode === "edit" ? "Ordem atualizada." : "Ordem de produção criada.");
      onDone();
    } catch (err) {
      if (isApiClientError(err) && err.error.code === "SCHEDULE_OVERLAP") {
        setConflict(err.error.message);
        return;
      }
      toast.error("Falha ao salvar a ordem.", {
        description: isApiClientError(err) ? err.error.message : "Erro inesperado.",
      });
    }
  }

  return (
    <form onSubmit={(e) => void form.handleSubmit(submit)(e)} className="space-y-4">
      {conflict ? (
        <div className="border-rd-danger-500/40 flex items-start gap-2 rounded-md border bg-rd-danger-50 p-3 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rd-danger-700" />
          <div className="flex-1">
            <p className="font-medium text-rd-danger-700">Conflito de janela na linha</p>
            <p className="text-text-secondary">{conflict} Ajuste o início/fim para salvar.</p>
          </div>
          <span className="rounded bg-card px-1.5 py-0.5 font-mono text-[11px] text-rd-danger-700">
            409
          </span>
        </div>
      ) : null}

      <ProductionOrderFormBody
        form={form}
        conflict={!!conflict}
        order={order}
        lockedLineId={lockedLineId}
      />

      <ProductionOrderFormActions
        mode={mode}
        pending={pending}
        conflict={!!conflict}
        onCancel={onCancel}
        onCancelOrder={onCancelOrder}
      />
    </form>
  );
}
